/**
 * Контракт системной кнопки «Назад» для мобильных Android-приложений (DomCRM Global/Тренер
 * и брендированные). Приложение открывает этот embed во вложенном iframe, поэтому
 * `WebView.canGoBack()` не видит pushState-переходы React Router. Здесь мы сообщаем родителю
 * глубину внутренней истории и выполняем шаг назад по его запросу.
 *
 * Сообщения не содержат секретов: только факт наличия/глубину внутренней истории.
 * Родитель (local Capacitor-бандл) подтверждает источник (`event.source === iframe`).
 */

export const EMBED_BACK_SOURCE = 'domcrm-embed' as const;

export type EmbedHistoryDepthMessage = {
  source: typeof EMBED_BACK_SOURCE;
  type: 'history-depth';
  /** Индекс текущей записи истории SPA (0 — корень, шагать некуда). */
  depth: number;
  canGoBack: boolean;
};

/** Минимальный контракт роутера: нужен только факт смены location. */
export type EmbedBackBridgeRouter = {
  subscribe: (listener: () => void) => () => void;
};

let started = false;

function isInIframe(): boolean {
  try {
    return window.parent !== window;
  } catch {
    return false;
  }
}

/**
 * Глубина внутренней истории SPA. `createBrowserRouter` хранит индекс записи
 * в `history.state.idx`; отсутствие индекса трактуем как корень.
 */
export function readEmbedHistoryDepth(): number {
  try {
    const state = window.history.state as { idx?: unknown } | null;
    const idx = state && typeof state.idx === 'number' ? state.idx : 0;
    if (!Number.isFinite(idx) || idx <= 0) return 0;
    return Math.floor(idx);
  } catch {
    return 0;
  }
}

function postDepth(depth: number): void {
  if (!isInIframe()) return;
  const message: EmbedHistoryDepthMessage = {
    source: EMBED_BACK_SOURCE,
    type: 'history-depth',
    depth,
    canGoBack: depth > 0
  };
  try {
    window.parent.postMessage(message, '*');
  } catch {
    /* ignore */
  }
}

/** Идемпотентно включает мост Back внутри iframe (в полноэкранном embed не нужен). */
export function initEmbedBackBridge(router: EmbedBackBridgeRouter): void {
  if (typeof window === 'undefined' || started || !isInIframe()) return;
  started = true;

  const publish = () => postDepth(readEmbedHistoryDepth());

  window.addEventListener('message', (event: MessageEvent) => {
    if (event.source !== window.parent) return;
    const data = event.data as { type?: unknown } | null;
    if (!data || typeof data.type !== 'string') return;
    if (data.type === 'domcrm:back') {
      if (readEmbedHistoryDepth() > 0) {
        window.history.back();
      } else {
        // Глубина устарела: сообщаем родителю, что шагать некуда.
        publish();
      }
      return;
    }
    if (data.type === 'domcrm:history-request') {
      publish();
    }
  });

  router.subscribe(publish);
  window.addEventListener('load', publish);
  publish();
}
